'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { motion } from 'motion/react';

import {
  LOADER_IMAGES,
  LOADER_COLORS,
  LOADER_EASE,
  OPEN_DELAY,
  OPEN_DURATION,
  BEAT_START,
  BEAT_INTERVAL,
  BEAT_COUNT,
  HOLE_REVEAL_LEAD,
  EXPAND_START,
  EXPAND_DURATION,
  LOADER_TOTAL,
  COLOR_FADE_DURATION,
  IMAGE_SCALE_FROM,
} from './timeline';

// First-load loader: a full-screen colour overlay with a centered image box.
// Images cut through in sync with the overlay colour; on the last beat the
// overlay is swapped for four frame panels around the image window, which
// then scale away outward until the hole swallows the whole viewport,
// revealing the (always fully rendered) .contentContainer underneath.
//
// The frame panels replace the old SVG hole mask — Safari/iOS ignores
// mask-image: url(#id) pointing at an SVG <mask>, so the expand never
// played on mobile. All geometry is derived in CSS from the --loader-w /
// --loader-h custom properties (see _pageLoader.scss), never measured in
// JS, so panels and image box stay aligned through mid-animation resizes.
//
// Mounted by HeroHome (the homepage hero) and nowhere else — it is the only
// block that ever plays a loader, so there is no path gate here. The root div
// and solid overlay are server-rendered so they cover the page from first
// paint, using the fixed LOADER_COLORS sequence rather than the inherited
// --color-dynamic-* vars (which are white until dynamicColors.js runs).
const FRAME_PANELS = [
  {
    key: 'top',
    prop: 'scaleY',
    style: {
      top: 0,
      left: 0,
      right: 0,
      height: 'calc((100% - var(--loader-h)) / 2)',
      transformOrigin: 'center top',
    },
  },
  {
    key: 'bottom',
    prop: 'scaleY',
    style: {
      bottom: 0,
      left: 0,
      right: 0,
      height: 'calc((100% - var(--loader-h)) / 2)',
      transformOrigin: 'center bottom',
    },
  },
  {
    key: 'left',
    prop: 'scaleX',
    style: {
      top: 0,
      bottom: 0,
      left: 0,
      width: 'calc((100% - var(--loader-w)) / 2)',
      transformOrigin: 'left center',
    },
  },
  {
    key: 'right',
    prop: 'scaleX',
    style: {
      top: 0,
      bottom: 0,
      right: 0,
      width: 'calc((100% - var(--loader-w)) / 2)',
      transformOrigin: 'right center',
    },
  },
];

function PageLoader() {
  const router = useRouter();
  const [done, setDone] = useState(false);
  const [beat, setBeat] = useState(0); // 0..BEAT_COUNT, advances on each image cut
  const [holeOn, setHoleOn] = useState(false);
  const [expanding, setExpanding] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setDone(true);
      return;
    }

    // Scroll lock and the nav hold are owned by HeroHome, which mounts this
    // component — see its loader effect.
    const timers = [];
    for (let i = 0; i < BEAT_COUNT; i++) {
      timers.push(
        setTimeout(
          () => setBeat(i + 1),
          (BEAT_START + i * BEAT_INTERVAL) * 1000
        )
      );
    }
    const lastImageOutTime = BEAT_START + (BEAT_COUNT - 1) * BEAT_INTERVAL;
    timers.push(
      setTimeout(
        () => setHoleOn(true),
        Math.max(0, lastImageOutTime - HOLE_REVEAL_LEAD) * 1000
      )
    );
    timers.push(setTimeout(() => setExpanding(true), EXPAND_START * 1000));
    timers.push(setTimeout(() => setDone(true), LOADER_TOTAL * 1000));

    const finish = () => setDone(true);
    router.events.on('routeChangeStart', finish);

    return () => {
      timers.forEach(clearTimeout);
      router.events.off('routeChangeStart', finish);
    };
  }, []);

  if (done) return null;

  const backgroundColor = `var(--color-${LOADER_COLORS[Math.min(beat, BEAT_COUNT)]})`;

  return (
    <div className="pageLoader">
      {!holeOn ? (
        // Solid overlay while the images play — swapped for the frame
        // panels just before the last cut, invisibly (same colour, and the
        // image box fully covers the window they leave open).
        <motion.div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'auto',
          }}
          initial={false}
          animate={{ backgroundColor }}
          transition={{ duration: COLOR_FADE_DURATION, ease: 'easeInOut' }}
        />
      ) : (
        FRAME_PANELS.map(({ key, prop, style }) => (
          <motion.div
            key={key}
            style={{
              position: 'absolute',
              ...style,
              pointerEvents: expanding ? 'none' : 'auto',
            }}
            initial={false}
            animate={{ [prop]: expanding ? 0 : 1, backgroundColor }}
            transition={{
              duration: EXPAND_DURATION,
              ease: LOADER_EASE,
              backgroundColor: {
                duration: COLOR_FADE_DURATION,
                ease: 'easeInOut',
              },
            }}
          />
        ))
      )}
      {!expanding && (
        <motion.div
          className="pageLoaderImages"
          initial={{ clipPath: 'inset(50% 0% 50% 0%)' }}
          animate={{ clipPath: 'inset(0% 0% 0% 0%)' }}
          transition={{
            delay: OPEN_DELAY,
            duration: OPEN_DURATION,
            ease: LOADER_EASE,
          }}
        >
          {LOADER_IMAGES.map((src, i) => {
            const style = {
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: BEAT_COUNT - i,
              opacity: beat > i ? 0 : 1,
            };
            // Only the first image animates — it eases back to 1 in step with
            // the clip path opening the window. The rest are uncovered by the
            // beat above them cutting out, and sit flat at scale 1.
            //
            // Only beat 0 gets fetchpriority="high": it is on screen at
            // OPEN_DELAY (0.5s) so it genuinely is urgent. Beats 1-3 don't
            // appear until 1.9s / 2.5s / 3.1s, and marking all four high meant
            // ~290KB of JPEG outranking the hero's own poster in the request
            // queue. They still load eagerly, just behind it.
            if (i > 0) {
              return (
                <img key={i} src={src} alt="" decoding="async" style={style} />
              );
            }
            return (
              <motion.img
                key={i}
                src={src}
                alt=""
                fetchpriority="high"
                decoding="async"
                style={style}
                initial={{ scale: IMAGE_SCALE_FROM }}
                animate={{ scale: 1 }}
                transition={{
                  delay: OPEN_DELAY,
                  duration: OPEN_DURATION,
                  ease: LOADER_EASE,
                }}
              />
            );
          })}
        </motion.div>
      )}
    </div>
  );
}

export default PageLoader;
